import { AlertTriangle, RefreshCcw } from "lucide-react";
import * as React from "react";
import { getUserMessage } from "../../lib/errors";
import { cn } from "../../lib/utils";
import { Button } from "./button";

interface ErrorStateProps {
  title?: string;
  description?: string;
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

export function ErrorState({
  title = "Something went wrong",
  description,
  error,
  onRetry,
  retryLabel = "Try again",
  className,
}: ErrorStateProps) {
  // Prefer an explicit description; otherwise derive a friendly, consistent
  // message from the error via the central mapper (handles ApiError statuses).
  // Guarded so "no error" still renders no detail line.
  const detail =
    description ?? (error != null ? getUserMessage(error) : undefined);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-lg border-destructive/30 bg-destructive/5 px-6 py-12 text-center",
        className
      )}
      role="alert"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle
          className="h-6 w-6 text-destructive"
          aria-hidden="true"
        />
      </div>
      <h3 className="text-h4">{title}</h3>
      {detail && (
        <p className="mt-1.5 max-w-md text-sm text-muted-foreground break-words">
          {detail}
        </p>
      )}
      {onRetry && (
        <Button onClick={onRetry} variant="outline" className="mt-6">
          <RefreshCcw className="mr-2 h-4 w-4" />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
